'use client';

import { usePathname, useRouter } from 'next/navigation';
import gsap from 'gsap';
import TransitionContext from './TransitionContext.jsx';
import { createContext, useContext, useEffect, useLayoutEffect, useRef } from 'react';
import { RouterName } from '@/lib/enum/RouterEnum';

const useIsoLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;
const READY_TIMEOUT_MS = 1000;

const isDetailPath = (path) => !!path?.includes(RouterName.projectDetail);

const NavigationContext = createContext({
  exit: (href) => {
    if (typeof window !== 'undefined') window.location.href = href;
  },
});
export const useTransitionExit = () => useContext(NavigationContext).exit;

const SwipeTransition = ({ children }) => {
  const pathname = usePathname();
  const router = useRouter();
  const { toggleCompleted, readyPath, readySeq } = useContext(TransitionContext);
  const nodeRef = useRef(null);
  const isProjectDetails = isDetailPath(pathname);

  const entryTlRef = useRef(null);
  const timeoutRef = useRef(null);
  const isNavigatingRef = useRef(false);

  const clearReadyTimeout = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const killEntryTl = () => {
    if (entryTlRef.current) {
      entryTlRef.current.kill();
      entryTlRef.current = null;
    }
  };

  const exit = (href) => {
    if (isNavigatingRef.current) return;
    const node = nodeRef.current;
    if (!node) {
      router.push(href);
      return;
    }
    isNavigatingRef.current = true;
    killEntryTl();
    clearReadyTimeout();
    toggleCompleted(false);
    router.prefetch(href);  // Prefetch the data from the next page while we animate the page transition.
    const leavingDetail = isProjectDetails;
    gsap.to(node, {
      yPercent: leavingDetail ? 100 : -100,
      autoAlpha: 0,
      duration: 0.2,
      overwrite: 'auto',
      onComplete: () => router.push(href),
    });
  };

  useIsoLayoutEffect(() => {
    const node = nodeRef.current;
    if (!node) return;

    gsap.killTweensOf(node);
    killEntryTl();
    clearReadyTimeout();

    gsap.set(node, { autoAlpha: 0, yPercent: isProjectDetails ? 100 : -100 });

    const tl = gsap.timeline({
      paused: true,
      onComplete: () => {
        toggleCompleted(true);
        isNavigatingRef.current = false;
        entryTlRef.current = null;
      },
    })
      .to(node, { autoAlpha: 1, yPercent: 0, duration: 0.2, ease: 'power2.out' });
      // .set(node, { clearProps: 'transform' })
    entryTlRef.current = tl;

    timeoutRef.current = setTimeout(() => {
      console.warn('[route-ready-timeout]', pathname);
      timeoutRef.current = null;
      const current = entryTlRef.current;
      if (current && current.progress() === 0 && !current.isActive()) {
        current.play(0);
      }
    }, READY_TIMEOUT_MS);

    return () => {
      killEntryTl();
      clearReadyTimeout();
    };
  }, [pathname, isProjectDetails]);

  useEffect(() => {
    if (readyPath !== pathname) return;
    const tl = entryTlRef.current;
    if (!tl) return;
    if (tl.isActive() || tl.progress() > 0) return;
    clearReadyTimeout();
    tl.play(0);
  }, [pathname, readyPath, readySeq]);

  return (
    <NavigationContext.Provider value={{ exit }}>
      <div ref={nodeRef} className="swipe-transition-panel">
        {children}
      </div>
    </NavigationContext.Provider>
  );
};

export default SwipeTransition;
